// API Endpoints
// Centralized enum for all API endpoints used across the application
// This makes it easy to update endpoints in one place

export enum ApiEndpoints {
  // ===== AUTHENTICATION ENDPOINTS =====
  AUTH_LOGIN = '/auth/login',
  AUTH_REGISTER = '/auth/register',
  AUTH_PROFILE = '/auth/profile',
  AUTH_CHANGE_PASSWORD = '/auth/change-password',

  // ===== USER MANAGEMENT ENDPOINTS =====
  USERS = '/users',
  USERS_BY_ID = '/users/:id',
  USERS_SEARCH = '/users/search',
  USERS_BULK_DELETE = '/users/bulk-delete',
  USERS_CHANGE_PASSWORD = '/users/:id/change-password',
  USERS_RESET_PASSWORD = '/users/:id/reset-password',

  // ===== CUSTOMER MANAGEMENT ENDPOINTS =====
  CUSTOMERS = '/customers',
  CUSTOMERS_BY_ID = '/customers/:id',
  CUSTOMERS_SEARCH = '/customers/search',
  CUSTOMERS_BULK_DELETE = '/customers/bulk-delete',
  CUSTOMERS_EXPORT = '/api/customers/export',

  // ===== PRODUCT MANAGEMENT ENDPOINTS =====
  PRODUCTS = '/products',
  PRODUCTS_BY_ID = '/products/:id',
  PRODUCTS_BY_CODE = '/products/code/:itemCode',
  PRODUCTS_SEARCH = '/products/search',
  PRODUCTS_BULK_DELETE = '/products/bulk-delete',
  PRODUCTS_CHECK_CODE = '/products/check-code/:itemCode',
  PRODUCTS_EXPORT = '/api/products/export',

  // ===== BILL MANAGEMENT ENDPOINTS =====
  BILLS = '/bills',
  BILLS_BY_ID = '/bills/:id',
  BILLS_BY_NUMBER = '/bills/number/:billNumber',
  BILLS_SEARCH = '/bills/search',
  BILLS_SELECT = '/bills/select',
  BILLS_EXPORT = '/api/bills/export',
  BILLS_SEND_EMAIL = '/bills/:id/send-email',
  BILLS_STATS = '/bills/stats',
  BILLS_HSN_SUMMARY = '/bills/hsn-summary',
  BILLS_DUPLICATE = '/bills/:id/duplicate',
  BILLS_MARK_PAID = '/bills/:id/mark-paid',
  BILLS_MARK_PENDING = '/bills/:id/mark-pending',
  BILLS_UPDATE_NOTE = '/bills/:id/note',
  BILLS_HISTORY = '/bills/:id/history',

  // ===== REPORTS ENDPOINTS =====
  REPORTS_STATS = '/reports/stats',

  // ===== UTILITY ENDPOINTS =====
  LOGIN_REDIRECT = '/login',
}

// Helper function to replace path parameters
export function buildEndpoint(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
  let url = String(endpoint);
  
  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`:${key}`, String(value));
  });
  
  return url;
}

// Helper function to build search URLs
export function buildSearchUrl(baseEndpoint: ApiEndpoints, query: string): string {
  return `${String(baseEndpoint)}?q=${encodeURIComponent(query)}`;
}

// Helper function to build export URLs
export function buildExportUrl(endpoint: ApiEndpoints, params: Record<string, any> = {}): string {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

// Type-safe endpoint builder for different HTTP methods
export class EndpointBuilder {
  static get(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
    return buildEndpoint(endpoint, params);
  }

  static post(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
    return buildEndpoint(endpoint, params);
  }

  static put(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
    return buildEndpoint(endpoint, params);
  }

  static delete(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
    return buildEndpoint(endpoint, params);
  }

  static patch(endpoint: ApiEndpoints, params: Record<string, string | number> = {}): string {
    return buildEndpoint(endpoint, params);
  }

  static search(baseEndpoint: ApiEndpoints, query: string): string {
    return buildSearchUrl(baseEndpoint, query);
  }

  static export(endpoint: ApiEndpoints, params: Record<string, any> = {}): string {
    return buildExportUrl(endpoint, params);
  }
}

// Convenience functions for common operations
export const Endpoints = {
  // User endpoints
  getUser: (id: string) => EndpointBuilder.get(ApiEndpoints.USERS_BY_ID, { id }),
  updateUser: (id: string) => EndpointBuilder.put(ApiEndpoints.USERS_BY_ID, { id }),
  deleteUser: (id: string) => EndpointBuilder.delete(ApiEndpoints.USERS_BY_ID, { id }),
  searchUsers: (query: string) => EndpointBuilder.search(ApiEndpoints.USERS_SEARCH, query),
  changeUserPassword: (id: string) => EndpointBuilder.post(ApiEndpoints.USERS_CHANGE_PASSWORD, { id }),
  resetUserPassword: (id: string) => EndpointBuilder.post(ApiEndpoints.USERS_RESET_PASSWORD, { id }),

  // Customer endpoints
  getCustomer: (id: string) => EndpointBuilder.get(ApiEndpoints.CUSTOMERS_BY_ID, { id }),
  updateCustomer: (id: string) => EndpointBuilder.put(ApiEndpoints.CUSTOMERS_BY_ID, { id }),
  deleteCustomer: (id: string) => EndpointBuilder.put(ApiEndpoints.CUSTOMERS_BY_ID, { id }),
  searchCustomers: (query: string) => EndpointBuilder.search(ApiEndpoints.CUSTOMERS_SEARCH, query),
  exportCustomers: (params?: Record<string, any>) => EndpointBuilder.export(ApiEndpoints.CUSTOMERS_EXPORT, params),

  // Product endpoints
  getProduct: (id: string) => EndpointBuilder.get(ApiEndpoints.PRODUCTS_BY_ID, { id }),
  getProductByCode: (itemCode: string) => EndpointBuilder.get(ApiEndpoints.PRODUCTS_BY_CODE, { itemCode }),
  updateProduct: (id: string) => EndpointBuilder.put(ApiEndpoints.PRODUCTS_BY_ID, { id }),
  deleteProduct: (id: string) => EndpointBuilder.put(ApiEndpoints.PRODUCTS_BY_ID, { id }),
  searchProducts: (query: string) => EndpointBuilder.search(ApiEndpoints.PRODUCTS_SEARCH, query),
  checkProductCode: (itemCode: string) => EndpointBuilder.get(ApiEndpoints.PRODUCTS_CHECK_CODE, { itemCode }),
  exportProducts: (params?: Record<string, any>) => EndpointBuilder.export(ApiEndpoints.PRODUCTS_EXPORT, params),

  // Bill endpoints
  getBill: (id: string) => EndpointBuilder.get(ApiEndpoints.BILLS_BY_ID, { id }),
  getBillByNumber: (billNumber: string) => EndpointBuilder.get(ApiEndpoints.BILLS_BY_NUMBER, { billNumber }),
  updateBill: (id: string) => EndpointBuilder.put(ApiEndpoints.BILLS_BY_ID, { id }),
  deleteBill: (id: string) => EndpointBuilder.put(ApiEndpoints.BILLS_BY_ID, { id }),
  searchBills: (query: string) => EndpointBuilder.search(ApiEndpoints.BILLS_SEARCH, query),
  sendBillEmail: (id: string) => EndpointBuilder.post(ApiEndpoints.BILLS_SEND_EMAIL, { id }),
  duplicateBill: (id: string) => EndpointBuilder.post(ApiEndpoints.BILLS_DUPLICATE, { id }),
  markBillPaid: (id: string) => EndpointBuilder.put(ApiEndpoints.BILLS_MARK_PAID, { id }),
  markBillPending: (id: string) => EndpointBuilder.put(ApiEndpoints.BILLS_MARK_PENDING, { id }),
  updateBillNote: (id: string) => EndpointBuilder.put(ApiEndpoints.BILLS_UPDATE_NOTE, { id }),
  getBillHistory: (id: string) => EndpointBuilder.get(ApiEndpoints.BILLS_HISTORY, { id }),
  exportBills: (params?: Record<string, any>) => EndpointBuilder.export(ApiEndpoints.BILLS_EXPORT, params),
} as const; 